'use client';

// ===================================================
// GAM Command Center — slash quick bar
// ===================================================
//
// Replaces the tall categorised popup that used to open on "/". A thin
// horizontal strip of icons sits just ABOVE the caret and is pulled toward
// the leading edge so it never runs into the sidebar on the right.
//
// Deliberately NOT rendered through the suggestion plugin's ReactRenderer +
// tippy. That popup was being torn down on its own a couple of seconds after
// opening (three fixes missed the cause), so this is a plain React component
// owning its own visibility: it opens when the extension reports a slash, and
// closes only on an explicit dismissal — Escape, a pick, or a click outside.
// A spurious plugin teardown can no longer take it with it.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/core';
import type { Range } from '@tiptap/core';
import type { SlashCommandItem } from './types';
import { iconForCommand } from './slash-command-icons';
import { pushRecent, quickBarTitles, readRecents } from './slash-recents';
import { EditorOverlay, EDITOR_OVERLAY_Z } from './editor-overlay';

/** Hover dwell before the name appears, per Gal: "after 1–2 seconds". */
const TOOLTIP_DELAY_MS = 1500;

interface SlashQuickBarProps {
  editor: Editor;
  items: SlashCommandItem[];
  /** Caret position reported by the extension, in viewport coordinates. */
  anchor: { top: number; left: number } | null;
  onDismiss: () => void;
  range: Range | null;
}

export function SlashQuickBar({ editor, items, anchor, onDismiss, range }: SlashQuickBarProps) {
  const barRef = useRef<HTMLDivElement>(null);
  const [recents, setRecents] = useState<string[]>([]);
  const [hovered, setHovered] = useState<string | null>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Read after mount only — localStorage during render would desync SSR.
  useEffect(() => {
    setRecents(readRecents());
  }, []);

  // Escape + click-outside are the ONLY things that close the bar.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    // Any mousedown that isn't ON the bar closes it — including inside the
    // editor. Deliberately NOT exempting the editor: since the bar no longer
    // closes when the extension exits, a deliberate click anywhere else is
    // the "clear action" that dismisses it. Continuing to TYPE never closes it.
    //
    // The one exemption is the Sidebar's תוספים panel, which mirrors this
    // same slash and carries data-gam-slash-menu-mirror. Without it, picking
    // a plugin there is self-defeating: this capture-phase mousedown fires
    // first, dismisses, the panel unmounts, and the click never reaches the
    // item. Matched by attribute rather than import because this package
    // cannot see app-level components — the same reason the bridge is an
    // event in the first place.
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (barRef.current?.contains(target)) return;
      if (
        target instanceof Element &&
        target.closest('[data-gam-slash-menu-mirror]')
      ) {
        return;
      }
      onDismiss();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown, true);
    };
  }, [editor, onDismiss]);

  useEffect(
    () => () => {
      if (hoverTimer.current) clearTimeout(hoverTimer.current);
    },
    []
  );

  const handleEnter = useCallback((title: string) => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setHovered(title), TOOLTIP_DELAY_MS);
  }, []);

  const handleLeave = useCallback(() => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setHovered(null);
  }, []);

  const run = useCallback(
    (item: SlashCommandItem) => {
      handleLeave();
      setRecents(pushRecent(item.title));
      // range comes from the extension and covers the "/" plus anything typed
      // after it, so running the command also removes the trigger text.
      if (range) item.command({ editor, range });
      onDismiss();
    },
    [editor, range, onDismiss, handleLeave]
  );

  if (!anchor) return null;

  const byTitle = new Map(items.map((i) => [i.title, i]));
  const shown = quickBarTitles(recents)
    .map((t) => byTitle.get(t))
    .filter((i): i is SlashCommandItem => Boolean(i));
  if (shown.length === 0) return null;

  const favouriteCount = shown.filter((i) => !recents.includes(i.title)).length;

  return (
    <EditorOverlay>
    <div
      ref={barRef}
      className="gam-slash-bar"
      dir="rtl"
      style={{
        position: 'fixed',
        top: anchor.top,
        left: anchor.left,
        zIndex: EDITOR_OVERLAY_Z,
      }}
      role="toolbar"
      aria-label="פקודות מהירות"
    >
      {shown.map((item, i) => {
        const Icon = iconForCommand(item.title);
        return (
          <span key={item.title} className="gam-slash-bar__slot">
            {/* hairline between the favourites and the recently-used half */}
            {i === favouriteCount && favouriteCount > 0 && <span className="gam-slash-bar__sep" />}
            <button
              type="button"
              className="gam-slash-bar__btn"
              onClick={() => run(item)}
              onMouseEnter={() => handleEnter(item.title)}
              onMouseLeave={handleLeave}
              aria-label={item.titleHe}
            >
              <Icon className="gam-slash-bar__icon" />
            </button>
            {hovered === item.title && (
              <span className="gam-slash-bar__tip" role="tooltip">
                {item.titleHe}
              </span>
            )}
          </span>
        );
      })}
    </div>
    </EditorOverlay>
  );
}
